<template>
  <router-view v-if="$route.name === links.iap.pricingTemplateDetails" />
  <router-view v-else-if="$route.name === links.iap.appPricingTemplateDetails" />
  <div
    v-else-if="[links.iap.pricingTemplates, links.iap.appPricingTemplates].includes($route.name)"
    class="pricing-template-list-wrapper"
  >
    <div v-if="showAppTemplates" class="actionsHeader">
      <Button label="New App Pricing Template" icon="pi pi-plus" @click="openTemplateCreationModal()" />
    </div>
    <DataTable
      :value="listedPricingTemplates"
      :loading="isLoading"
      selection-mode="single"
      data-key="id"
      class="primevue-migrated"
      removable-sort
      rounded
      sort-field="linkedIAPsCount"
      :sort-order="-1"
      @row-select="selectRow"
    >
      <template #empty>No App Pricing Templates available yet. Create one now!</template>
      <Column header="Name" field="name"></Column>
      <Column header="Default price" field="defaultPriceAmountMicros">
        <template #body="slotProps">
          {{ formatPrice(slotProps.data.defaultPriceAmountMicros / 1000000, slotProps.data.defaultPriceCurrency) }}
        </template>
      </Column>
      <Column header="Linked IAPs" field="linkedIAPsCount" sortable></Column>
      <Column header="Last updated" field="updatedAt" sortable>
        <template #body="slotProps">
          {{ formatDate(slotProps.data.updatedAt) }}
        </template>
      </Column>
      <Column style="width: 3rem">
        <template #body>
          <i class="pi pi-angle-right" />
        </template>
      </Column>
    </DataTable>
    <CreatePricingTemplateDialog v-model:visible="isCreationModalVisible" @submit="loadPriceTemplates" />
  </div>
</template>

<script setup lang="ts">
import { Button, Column, DataTable, DataTableRowSelectEvent } from "primevue";
import { formatPrice } from "@/utils/formatPrice";
import { formatDate } from "@/utils/formatDateTime";
import usePricingTemplates from "@/modules/IAP/hooks/usePricingTemplates";
import { onMounted, ref, watch } from "vue";
import links from "@/router/links";
import { useRoute, useRouter } from "vue-router";
import CreatePricingTemplateDialog from "@/modules/IAP/components/CreatePricingTemplateDialog.vue";
import { PricingTemplate } from "@/modules/IAP/types";

const { getAppPriceTemplates, getSystemPriceTemplates, appPricingTemplates, systemPricingTemplates } =
  usePricingTemplates();
const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const gameId = ref(route.params.gameId);
const showAppTemplates = ref(route.meta.showAppTemplates);
const listedPricingTemplates = ref<PricingTemplate[]>([]);
const isCreationModalVisible = ref(false);

const selectRow = (event: DataTableRowSelectEvent) => {
  if (showAppTemplates.value) {
    router.push({ name: links.iap.appPricingTemplateDetails, params: { templateId: event.data.id } });
  } else {
    router.push({ name: links.iap.pricingTemplateDetails, params: { templateId: event.data.id } });
  }
};

const openTemplateCreationModal = () => {
  isCreationModalVisible.value = true;
};

const loadPriceTemplates = async () => {
  if (showAppTemplates.value) {
    await getAppPriceTemplates(gameId.value as string);
    listedPricingTemplates.value = appPricingTemplates.value;
  } else {
    await getSystemPriceTemplates(gameId.value as string);
    listedPricingTemplates.value = systemPricingTemplates.value;
  }
};

onMounted(async () => {
  isLoading.value = true;
  await loadPriceTemplates();
  isLoading.value = false;
});

watch(
  () => route.meta,
  async (newMeta: any) => {
    isLoading.value = true;
    showAppTemplates.value = newMeta.showAppTemplates;
    await loadPriceTemplates();
    isLoading.value = false;
  }
);
</script>

<style scoped lang="scss">
.actionsHeader {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.tableActions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.pricing-template-list-wrapper {
  :deep(.p-datatable) {
    border: 1px solid var(--vd-color-text-light-7);
    border-radius: var(--vd-border-radius-2);

    .p-datatable-tbody td {
      padding: 22px 14px;
    }

    .p-datatable-thead {
      th {
        background-color: var(--vd-color-text-light-9);
        padding: 12px 14px;
      }

      th:first-child {
        border-top-left-radius: var(--vd-border-radius-2);
      }

      th:last-child {
        border-top-right-radius: var(--vd-border-radius-2);
      }
    }

    .p-datatable-tbody tr:last-child {
      &:not(:hover) td {
        background-color: var(--vd-color-neutral-light);
      }

      td {
        border-bottom: none;
      }

      td:first-child {
        border-bottom-left-radius: var(--vd-border-radius-2);
      }

      td:last-child {
        border-bottom-right-radius: var(--vd-border-radius-2);
      }
    }
  }
}
</style>
